<template>
<div>
  <b-row class="history-variable-block" :class="{ 'noSliderBlock': !slider }" :id="elementId">
    <b-col cols="12" :md="labelColumnSize" :lg="labelColumnSize" :class=" { 'pb-2': verticalMode, 'text-center': centered }">
      <span class="d-inline-block history-label">{{ getLabel }}<br><span class="d-inline-block small" v-if="date && inputDisabled && slider && !hideDate">({{ getFormattedDate }})</span></span>
    </b-col>
    <b-col :cols="!editOrGraphsVisible || slider ? 12 : !inputDisabled ? 10 : 6" :md="valueColumnSize" :lg="valueColumnSize">
			<div v-if="!alreadySavedDailyVariable">
				<span class="history-variable-value" v-if="inputDisabled && !slider">{{ localizedInputValue }}</span>
				<base-select v-if="isBlood && !inputDisabled" v-model="processedValue" :placeholder="$t('selectOneOption')" label="name" valueProp="id" searchable filtered :options="bloodTypes" />
				<b-form-input size="sm" :id="id" :placeholder="placeholder" v-model="localizedInputValue" v-if="!isBlood && !inputDisabled && !variableValuesAreKeyPair && !slider" class="d-inline-block"></b-form-input>
				<clinic-history-variable-slider v-if="slider" :value="localizedInputValue" :name="name" :variableName="variableName" :rawData="rawData" :disabled="!editable || alreadySavedDailyVariable" @save="emitSave" :emojiType="VARIABLES_WITH_EMOJI_INPUT.includes(name)"></clinic-history-variable-slider>
				<span>{{ getMeasureUnit }}</span>
				<span class="d-inline-block small" v-if="date && inputDisabled && !slider">({{ getFormattedDate }})</span>
			</div>
			<div v-else class="overlapping-slider-text" :class="{ 'text-center': centered }">
				<span class="small">
  {{ $tc('clinic-history.already-saved-today-variable',
         daysRemaining,
         { days: frequencyDays, daysRemaining } ) }}
</span>
			</div>
    </b-col>
    <b-col :cols="!inputDisabled ? 2 : 6" md="3" lg="2" v-if="editOrGraphsVisible">
      <base-icon class="d-inline-block clickable icon" v-if="editable && inputDisabled && !slider" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/pencil-icon-blue.svg" @click.native="edit"></base-icon>
      <base-icon class="d-inline-block clickable icon" v-if="!inputDisabled" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/tick-icon-blue.svg" @click.native="save" :color="BLUE_COLOUR"></base-icon>
      <base-icon class="d-inline-block clickable icon" v-if="graphEnabled && inputDisabled" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/graph-icon-blue.svg" @click.native="showChart"></base-icon>
      <base-icon class="d-inline-block clickable icon" :green="rawData.trackingCreated" :gray="!rawData.trackingCreated" v-if="editable && inputDisabled && !isBlood && showTrackingButton" src="/static-appopensalud/img/icons/morillas-svg-icons/primary/bell.svg" @click.native="showReminder = true"></base-icon>
    </b-col>
  </b-row>
  <base-modal v-model="showChartModal" @hidden="showChartModal = false">
    <clinic-history-variable-chart :variableName="variableName" :coloringPoints="VARIABLES_WITH_COLOUR_RANGE.includes(name)" :dynamicXAxis="VARIABLES_WITH_DYNAMIC_X_AXIS.includes(name)" :oneValuePerDay="DAILY_VARIABLES.includes(name) || daily" :maxValue="VARIABLES_WITH_COLOUR_RANGE.includes(name) ? 10 : null"></clinic-history-variable-chart>
  </base-modal>
  <history-variable-reminder v-model="showReminder" :varName="variableName" :created="rawData.trackingCreated" @reminderCreated="rawData.trackingCreated = $event"></history-variable-reminder>
</div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import { FEEDING_QUALITY, SLEEP_QUALITY, EXERCISE_AMOUNT, HEALTH_CONDITION, HEALTH_CONDITION_TODAY, STRESS, HEIGHT, WEIGHT, BLOOD_TYPE, BLOOD_PRESSURE, FLOAT_VARIABLES } from '@/constants/clinicHistory'
import ClinicHistoryVariableSlider from './ClinicHistoryVariableSlider.vue'
import { BLUE_COLOUR, RED_COLOUR, GREEN_COLOUR } from '@/constants/colours'
import BaseModal from '../base/BaseModal.vue'
import ClinicHistoryVariableChart from '@/components/shared/ClinicHistoryVariableChart.vue'
import HistoryVariableReminder from '@/components/shared/HistoryVariableReminder'
import i18nMixin from '@/mixins/i18n.mixin'
import { getDDMMYYYY } from '@/helpers/date.helpers'

const I18N_FILE = 'clinic-history'
const VARIABLES_WITH_COLOUR_RANGE = [FEEDING_QUALITY, SLEEP_QUALITY, STRESS, EXERCISE_AMOUNT, HEALTH_CONDITION, HEALTH_CONDITION_TODAY]
const VARIABLES_WITH_DYNAMIC_X_AXIS = [FEEDING_QUALITY, SLEEP_QUALITY, STRESS, EXERCISE_AMOUNT, HEALTH_CONDITION, HEALTH_CONDITION_TODAY]
const DAILY_VARIABLES = [FEEDING_QUALITY, SLEEP_QUALITY, EXERCISE_AMOUNT, HEALTH_CONDITION, HEALTH_CONDITION_TODAY, STRESS]
const VARIABLES_WITH_EMOJI_INPUT = [HEALTH_CONDITION_TODAY]
const SAVE_EVENT = 'save'

export default {
  name: 'clinic-history-variable',
	mixins: [i18nMixin(I18N_FILE)],
  components: {
    BFormInput,
    ClinicHistoryVariableSlider,
    BaseModal,
    HistoryVariableReminder,
		ClinicHistoryVariableChart
  },
  props: {
    rawData: {
      type: Object,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      required: true
    },
    variableName: {
      type: String,
      required: false,
      default: null
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    graphEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    slider: {
      type: Boolean,
      required: false,
      default: false
    },
		label: {
			type: String,
			required: false,
			default: null
		},
		hideDate: {
			type: Boolean,
			required: false,
			default: false
		},
		verticalMode: {
			type: Boolean,
			required: false,
			default: false
		},
		showGraphByDefault: {
			type: Boolean,
			required: false,
			default: false
		},
    placeholder: String,
		daily: {
			type: Boolean,
			required: false,
			default: false
		},
		centered: {
			type: Boolean,
			required: false,
			default: false
		},
		showTrackingButton: {
			type: Boolean,
			required: false,
			default: true
		},
    frequencyDays: {
      type: Number,
      default: 1
    }
  },
	mounted () {
		if (this.showGraphByDefault) {
			this.showChart()
		}
	},
  created () {
    this.id = `history-var-input-${this._uid}`
    this.date = this.rawData.dateTime
    if (this.value && this.value.flat().length === 0) {
      this.processedValue = 0
    } else if (!this.value) {
      this.processedValue = 0
    } else if (this.value.flat().length === 1) {
      this.processedValue = this.value.flat()[0]
    } else {
      this.processedValue = this.value.flat()
    }
    this.$nextTick(() => {
      if (this.variableValuesAreKeyPair) {
        this.processedValue = this.variableValuesAreMultiple ? (this.processedValue.map(item => item.value[this.$i18nHelpers.getNormalizedLocale()]) || this.processedValue.value) : (this.processedValue.value[this.$i18nHelpers.getNormalizedLocale()] || this.processedValue.value)
      }
    })
  },
  data () {
    return {
			VARIABLES_WITH_EMOJI_INPUT,
			VARIABLES_WITH_COLOUR_RANGE,
			VARIABLES_WITH_DYNAMIC_X_AXIS,
			DAILY_VARIABLES,
      id: null,
      inputDisabled: true,
      processedValue: null,
      date: null,
      STRESS,
      BLUE_COLOUR,
      GREEN_COLOUR,
      RED_COLOUR,
      bloodTypes: [],
      showChartModal: false,
      showReminder: false,
      daysRemaining: 0
    }
  },
  methods: {
    edit () {
      if (this.variableName === BLOOD_TYPE) {
          if (this.bloodTypes.length === 0) {
            ClinicHistoryRepository().getBloodTypes().then(data => {
              this.bloodTypes = data.data
              if (this.processedValue) {
                this.processedValue = this.bloodTypes.find(b => b.name === this.processedValue.trim()).id
              }
            })
          } else if (this.processedValue) {
            this.processedValue = this.bloodTypes.find(b => b.name === this.processedValue.trim()).id
          }
      }
      this.inputDisabled = false
    },
    showChart () {
			this.showChartModal = true
    },
    save () {
      let data = {...this.rawData}
      data.values = [this.processedValue]

			if (this.isBloodPressure && data.values.length > 0 && (!data.values[0] || data.values[0].indexOf('/') < 0 || !data.values[0].split('/')[0] || !data.values[0].split('/')[1] || isNaN(data.values[0].split('/')[0]) || isNaN(data.values[0].split('/')[1]))) {
        this.$store.dispatch('showDangerAlert', this.$t('presureVarSaveError'))
        return
      } else if (data.variableName !== BLOOD_PRESSURE && data.values.length > 0 && isNaN(data.values[0])) {
        this.$store.dispatch('showDangerAlert', this.$t('isNanError'))
        return
      }
      ClinicHistoryRepository().saveVariableValue(data).then((response) => {
        if (response.data.data) {
          this.date = new Date()
          this.inputDisabled = true
          if (this.name === WEIGHT || this.name === HEIGHT || this.name === BLOOD_TYPE) {
            this.$emit('updateRawData', {varName: this.name, value: this.isBlood ? {key: this.processedValue, value: this.bloodTypes.find(b => b.id === this.processedValue).name} : this.processedValue})
          }
					this.emitSave(data.variableName, data.values)
        }
      })
    },
		emitSave (variableName, values) {
      this.date = new Date()
			this.$emit(SAVE_EVENT, variableName, values)
		}
  },
  computed: {
    localizedInputValue: {
      get () {
        const val = this.processedValue
        if (this.isBloodPressure) {
          return val || ''
        }
        if (val === '' || val === null || typeof val === 'undefined') {
          return ''
        }
        if (typeof val === 'string' && /[.,]$/.test(val)) {
          return val
        }
        const locale = this.$i18n.locale
        if (typeof val === 'string') {
          const normalized = val.replace(',', '.')
          const parsed = parseFloat(normalized)
          if (!isNaN(parsed)) {
            const isInt = this.isIntegerVariable || Number.isInteger(parsed)

            return new Intl.NumberFormat(locale, {
              minimumFractionDigits: isInt ? 0 : 1,
              maximumFractionDigits: isInt ? 0 : 2
            }).format(parsed)
          } else {
            return val
          }
        }
        if (typeof val === 'number') {
          const isInt = this.isIntegerVariable || Number.isInteger(val)

          return new Intl.NumberFormat(locale, {
            minimumFractionDigits: isInt ? 0 : 1,
            maximumFractionDigits: isInt ? 0 : 2
          }).format(val)
        }

        return val
      },
      set (val) {
        if (this.isBloodPressure) {
          this.processedValue = val
          return
        }
        if (val === '') {
          this.processedValue = ''
          return
        }

        if (/[.,]$/.test(val)) {
          this.processedValue = val
          return
        }

        const normalized = val.replace(',', '.')
        const parsed = parseFloat(normalized)

        if (!isNaN(parsed)) {
          this.processedValue = this.isIntegerVariable ? parseInt(parsed) : parsed
        } else {
          this.processedValue = val
        }
      }
    },
    isBloodPressure () {
      return this.name === BLOOD_PRESSURE
    },
    isIntegerVariable () {
      return !FLOAT_VARIABLES.includes(this.name)
    },
    getLabel () {
      return this.label || this.$t(`${this.name}`)
    },
    variableValuesAreMultiple () {
      return Array.isArray(this.processedValue) && this.processedValue.length > 1
    },
    variableValuesAreKeyPair () {
      const value = this.variableValuesAreMultiple ? this.processedValue[0] : this.processedValue
      return value.key && value.value != null
    },
    getFormattedDate () {
      return getDDMMYYYY(this.date)
    },
    getMeasureUnit () {
      const unitMsgKey = `measureUnits.${this.name}`
      return !this.$t(unitMsgKey).includes('measureUnits.') ? ` ${this.$t(unitMsgKey)}` : ''
    },
    showVariable () {
      return Array.isArray(this.processedValue) ? this.processedValue.length > 0 : this.processedValue
    },
    isBlood () {
      return this.variableName === BLOOD_TYPE
    },
		alreadySavedDailyVariable () {
      if (!(this.daily || DAILY_VARIABLES.includes(this.variableName))) return false

      // Sin registros anteriores → se puede usar
      if (!this.date) return false

      const MS_PER_DAY = 1000 * 60 * 60 * 24
      const last = new Date(this.date)
      const today = new Date()
      last.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      const diffDays = Math.floor((today - last) / MS_PER_DAY)
      this.daysRemaining = this.frequencyDays - diffDays
      return diffDays < this.frequencyDays
    },
		editVisible () {
			return this.editable && this.inputDisabled && !this.slider
		},
		graphVisible () {
			return this.graphEnabled && this.inputDisabled
		},
		editOrGraphsVisible () {
			return this.editVisible || !this.inputDisabled || this.graphVisible || this.slider
		},
		labelColumnSize () {
			return this.verticalMode ? 12 : 5
		},
		valueColumnSize () {
			return this.verticalMode ? 12 : 4
		},
		elementId () {
			return `${this.variableName}`
		}
  }
}
</script>

<style lang="scss">
.history-variable-block {
  .icon {
    margin-left: 10px;
  }

  .history-variable-value {
    color: $black;
  }

  input {
     width: 100px;

    &[readonly] {
      background-color: transparent;
      border: 0px;
      box-shadow: 0 0 0 0;
      color: $black;
    }
  }

  span {
    color: $gray-600;
  }
}

.unit-offset {
  position: absolute;
  margin-left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.hidden{
  visibility: hidden;
}
.history-label{
  font-weight: bold;
}
</style>


// WEBPACK FOOTER //
// src/components/shared/ClinicHistoryVariable.vue